import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cn } from '@repo/lib/cn';
import { cva } from 'class-variance-authority';

export const badgeVariants = cva(
  'group/badge transition-color rounded-md duration-150 relative inline-flex w-fit shrink-0 items-center gap-1 truncate border-transparent font-medium overflow-hidden whitespace-nowrap [&>svg]:size-3 [&>svg]:pointer-events-none  outline-0 focus-visible:outline-3 outline-focus-ring-primary aria-invalid:outline-focus-ring-destructive/35 aria-invalid:outline-focus-ring-destructive/75',
  {
    variants: {
      variant: {
        brand: 'text-brand bg-brand-accent hover:bg-brand-accent-hover',
        muted: 'bg-accent hover:bg-accent/80 text-accent-foreground',
        outline: 'text-accent-foreground bg-input dark:bg-input/35 hover:bg-input-focus dark:hover:bg-input-focus/75',
        orange: 'text-orange bg-orange-accent hover:bg-orange-accent-hover',
        success: 'text-success bg-success-accent hover:bg-success-accent-hover',
        warning: 'text-warning bg-warning-accent hover:bg-warning-accent-hover',
        destructive: 'text-destructive bg-destructive-accent hover:bg-destructive-accent-hover',
        purple: 'text-purple bg-purple-accent hover:bg-purple-accent-hover',
        turquoise: 'text-turquoise bg-turquoise-accent hover:bg-turquoise-accent-hover',
        blue: 'text-blue bg-blue-accent hover:bg-blue-accent-hover',
      },
      border: {
        true: 'border',
        false: 'border-transparent',
      },
      size: {
        sm: 'h-6 px-2 py-0.5 text-xs [&>svg]:h-3 [&>svg]:w-3 [&>svg]:shrink-0',
        md: 'h-7 px-3 py-1 text-sm [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0',
      },
    },
    compoundVariants: [
      {
        variant: 'muted',
        border: true,
        className: 'border-border-primary',
      },
      {
        variant: 'outline',
        border: true,
        className: 'border-border-primary',
      },
      {
        variant: 'brand',
        border: true,
        className: 'border-border-brand-accent',
      },
      {
        variant: 'orange',
        border: true,
        className: 'border-border-orange-accent',
      },
      {
        variant: 'success',
        border: true,
        className: 'border-border-success-accent',
      },
      {
        variant: 'warning',
        border: true,
        className: 'border-border-warning-accent',
      },
      {
        variant: 'destructive',
        border: true,
        className: 'border-border-destructive-accent',
      },
      {
        variant: 'purple',
        border: true,
        className: 'border-border-purple-accent',
      },
      {
        variant: 'turquoise',
        border: true,
        className: 'border-border-turquoise-accent',
      },
      {
        variant: 'blue',
        border: true,
        className: 'border-border-blue-accent',
      },
    ],
    defaultVariants: {
      size: 'sm',
      variant: 'brand',
      border: false,
    },
  }
);

export interface BadgeProps extends VariantProps<typeof badgeVariants>, ComponentProps<'span'> {
  asChild?: boolean;
}

export function Badge(badgeProps: BadgeProps) {
  const { className, variant, asChild = false, border, size, ...props } = badgeProps;
  const Comp = asChild ? Slot : 'span';

  return <Comp data-slot="badge" className={cn(badgeVariants({ variant, size, border }), className)} {...props} />;
}
